import * as React from "react";
import { useNavigate } from "react-router";

import Box from "@mui/material/Box";
import Fade from "@mui/material/Fade";

import type { HomePromptDTO, HomePromptOption } from "@volley/data";

import { ulid } from "../../../util/ulid";
import useDialog from "../../Dialog/useDialog";
import { useSelectedSport } from "../../common/context/sport";
import { useCurrentUser } from "../../hooks/currentUser";

import AskVolleyOverlay from "./AskVolleyOverlay";
import AskVolleyWidget from "./AskVolleyWidget";
import { fetchNextPrompt, recordPromptEvent } from "./api";
import { clearChatSession, loadChatSession } from "./chatSession";
import { FINISH_ASSESSMENT_VALUE, statsRouteForSport } from "./constants";
import type { ThreadMessage } from "./types";
import { useAssessmentLaunch } from "./useAssessmentLaunch";
import { useDuprConnect } from "./useDuprConnect";

/** Why the first-run spotlight ended (logged for funnel analysis). */
type SpotlightDismissReason = "scrim" | "got_it" | "callout";

/** localStorage key for the once-per-user first-run spotlight. */
function spotlightSeenKey(userId: number | undefined): string {
    return `askVolley.spotlightSeen.v1.${userId ?? "anon"}`;
}

function hasSeenSpotlight(userId: number | undefined): boolean {
    try {
        return localStorage.getItem(spotlightSeenKey(userId)) === "1";
    } catch {
        return true; // No storage access → don't nag.
    }
}

function markSpotlightSeen(userId: number | undefined): void {
    try {
        localStorage.setItem(spotlightSeenKey(userId), "1");
    } catch {
        // Ignore — the spotlight is non-essential.
    }
}

interface AskVolleyProps {
    /**
     * Rendered when the fetch has resolved with no eligible prompt for the user
     * (e.g. none seeded for their sport, or they've seen everything). Lets the
     * home screen fall back to its normal content instead of showing a gap.
     * Not shown while loading, nor after the user soft-dismisses the widget.
     */
    fallback?: React.ReactNode;
}

/**
 * Home-screen "Ask Volley" experience: fetches the highest-priority prompt for
 * the current user, shows it in an inline widget, runs a one-time first-run
 * spotlight to guarantee discovery, and opens the chat overlay on tap.
 *
 * Renders nothing while loading or after a soft-dismiss; once the fetch
 * resolves with no prompt, renders `fallback` (if provided).
 */
export default function AskVolley({
    fallback,
}: AskVolleyProps): React.ReactNode {
    const { currentUser } = useCurrentUser();
    const { setDialogType } = useDialog();
    const { selected: sport, getIdFromSport } = useSelectedSport();
    const navigate = useNavigate();
    const userId = currentUser?.id;
    const { launchAssessment, dialog: assessmentDialog } =
        useAssessmentLaunch();
    const { launchDuprConnect, dialog: duprDialog } = useDuprConnect();

    const [prompt, setPrompt] = React.useState<HomePromptDTO | null>(null);
    // False until the first fetch resolves, so we render neither the widget nor
    // the fallback during the loading window (avoids a carousel→widget flash).
    const [loaded, setLoaded] = React.useState(false);
    const [impressionUlid, setImpressionUlid] = React.useState<string>("");
    const [open, setOpen] = React.useState(false);
    const [initialOption, setInitialOption] = React.useState<
        string | undefined
    >(undefined);
    const [freeTextSeed, setFreeTextSeed] = React.useState<string | undefined>(
        undefined,
    );
    // A chat restored after the user backed out of a workout detail page. When
    // set, the overlay re-opens seeded from it instead of fetching fresh.
    const [restored, setRestored] = React.useState<
        | { thread: ThreadMessage[]; remainingOptions: HomePromptOption[] }
        | undefined
    >(undefined);
    // Bumped to force a fresh prompt fetch after a restored chat is dismissed
    // (the restore path skips the fetch, so dismissal must re-trigger it).
    const [reloadNonce, setReloadNonce] = React.useState(0);
    const [spotlight, setSpotlight] = React.useState(false);
    // Set when the user soft-dismisses the prompt (e.g. "Maybe later"); hides
    // the widget for the rest of this load.
    const [dismissed, setDismissed] = React.useState(false);

    // Widget wrapper, observed to log when the prompt actually enters the
    // viewport (vs. merely being rendered below the fold).
    const widgetRef = React.useRef<HTMLDivElement>(null);
    // Guards the one-per-impression viewport_visible event.
    const viewportLoggedUlid = React.useRef<string>("");

    const sportId = React.useMemo(() => {
        try {
            return getIdFromSport(sport);
        } catch {
            return undefined;
        }
    }, [getIdFromSport, sport]);

    // Fetch the next prompt whenever the sport changes; record an impression
    // and arm the first-run spotlight when one arrives.
    React.useEffect(() => {
        let cancelled = false;

        // Restore a chat the user left to view a workout, instead of fetching a
        // fresh prompt. Consume-once: clear it so it re-opens exactly once per
        // round-trip (a tap from the restored chat re-arms it). No new impression
        // and no spotlight — this is a continuation of the same view.
        const snapshot = loadChatSession(userId);
        if (snapshot) {
            clearChatSession(userId);
            setPrompt(snapshot.prompt);
            setImpressionUlid(snapshot.impressionUlid);
            setInitialOption(snapshot.initialOptionValue);
            setFreeTextSeed(snapshot.freeTextSeed);
            setRestored({
                thread: snapshot.thread,
                remainingOptions: snapshot.remainingOptions,
            });
            setDismissed(false);
            setOpen(true);
            setLoaded(true);
            return () => {
                cancelled = true;
            };
        }

        async function load() {
            try {
                const next = await fetchNextPrompt(sportId);
                if (cancelled) return;
                if (!next) {
                    setPrompt(null);
                    return;
                }
                const id = ulid();
                setPrompt(next);
                setImpressionUlid(id);
                setDismissed(false);
                recordPromptEvent({
                    eventType: "impression",
                    prompt: next,
                    impressionUlid: id,
                    sportId,
                });
                if (!hasSeenSpotlight(userId)) {
                    setSpotlight(true);
                }
            } catch {
                if (!cancelled) setPrompt(null);
            } finally {
                if (!cancelled) setLoaded(true);
            }
        }
        void load();
        return () => {
            cancelled = true;
        };
    }, [sportId, userId, reloadNonce]);

    // Dismiss the first-run spotlight. A `reason` (timer / scrim / got_it) logs
    // a spotlight_dismiss event; calls without one (e.g. when opening the chat)
    // are silent, since that path already logs its own interaction.
    const dismissSpotlight = React.useCallback(
        (reason?: SpotlightDismissReason) => {
            if (!spotlight) return;
            markSpotlightSeen(userId);
            if (reason && prompt) {
                recordPromptEvent({
                    eventType: "interaction",
                    prompt,
                    impressionUlid,
                    sportId,
                    metadata: { action: "spotlight_dismiss", reason },
                });
            }
            setSpotlight(false);
        },
        [spotlight, userId, prompt, impressionUlid, sportId],
    );

    // Log when the widget actually scrolls into view (once per impression), so
    // "saw it and ignored it" can be told apart from "never noticed it". Falls
    // back gracefully where IntersectionObserver is unavailable.
    React.useEffect(() => {
        const node = widgetRef.current;
        if (!prompt || !impressionUlid || !node) return undefined;
        if (typeof IntersectionObserver === "undefined") return undefined;

        const observer = new IntersectionObserver(
            (entries) => {
                const seen = entries.some((e) => e.isIntersecting);
                if (!seen) return;
                if (viewportLoggedUlid.current === impressionUlid) return;
                viewportLoggedUlid.current = impressionUlid;
                recordPromptEvent({
                    eventType: "impression",
                    prompt,
                    impressionUlid,
                    sportId,
                    metadata: { action: "viewport_visible" },
                });
                observer.disconnect();
            },
            { threshold: 0.5 },
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [prompt, impressionUlid, sportId]);

    const handleOpen = React.useCallback(
        (optionValue?: string) => {
            dismissSpotlight();
            setRestored(undefined);
            setFreeTextSeed(undefined);
            setInitialOption(optionValue);
            setOpen(true);
            if (prompt) {
                recordPromptEvent({
                    eventType: "interaction",
                    prompt,
                    impressionUlid,
                    sportId,
                    metadata: {
                        action: "open",
                        ...(optionValue ? { answerValue: optionValue } : {}),
                    },
                });
            }
        },
        [dismissSpotlight, impressionUlid, prompt, sportId],
    );

    // The open "Ask anything" field: capture the free text, then open the chat
    // seeded with it. The overlay sends the text to the LLM recommender and shows
    // the returned content; here we just log the free_text interaction and hand
    // off the seed.
    const handleAskAnything = React.useCallback(
        (text: string) => {
            dismissSpotlight();
            if (prompt) {
                recordPromptEvent({
                    eventType: "interaction",
                    prompt,
                    impressionUlid,
                    sportId,
                    metadata: { action: "free_text", text },
                });
            }
            setRestored(undefined);
            setInitialOption(undefined);
            setFreeTextSeed(text);
            setOpen(true);
        },
        [dismissSpotlight, impressionUlid, prompt, sportId],
    );

    // A soft-dismiss chip (e.g. "Maybe later"): capture the choice and hide the
    // widget for this load rather than opening the chat or routing to content.
    const handleDismiss = React.useCallback(
        (option: HomePromptOption) => {
            dismissSpotlight();
            if (prompt) {
                recordPromptEvent({
                    eventType: "interaction",
                    prompt,
                    impressionUlid,
                    sportId,
                    metadata: {
                        action: "dismiss",
                        answerValue: option.value,
                        answerLabel: option.label,
                    },
                });
            }
            setDismissed(true);
        },
        [dismissSpotlight, impressionUlid, prompt, sportId],
    );

    // An assessment-launch chip ("Start assessment" / "Finish assessment"):
    // drive the assessment flow directly instead of opening the chat — resume
    // an in-progress assessment, or open the all-skills assessment start dialog.
    const handleLaunchAssessment = React.useCallback(
        (option: HomePromptOption) => {
            dismissSpotlight();
            if (prompt) {
                recordPromptEvent({
                    eventType: "interaction",
                    prompt,
                    impressionUlid,
                    sportId,
                    metadata: {
                        action:
                            option.value === FINISH_ASSESSMENT_VALUE
                                ? "assessment_resume"
                                : "assessment_start",
                        answerValue: option.value,
                        answerLabel: option.label,
                    },
                });
            }
            void launchAssessment(option, sport);
        },
        [
            dismissSpotlight,
            impressionUlid,
            prompt,
            launchAssessment,
            sport,
            sportId,
        ],
    );

    // The "Connect DUPR" chip on the profile prompt: open the existing DUPR
    // connect flow directly instead of opening the chat (the user links a
    // verified rating; "I don't have a rating" is the paired soft-dismiss).
    const handleConnectDupr = React.useCallback(
        (option: HomePromptOption) => {
            dismissSpotlight();
            if (prompt) {
                recordPromptEvent({
                    eventType: "interaction",
                    prompt,
                    impressionUlid,
                    sportId,
                    metadata: {
                        action: "dupr_connect",
                        answerValue: option.value,
                        answerLabel: option.label,
                    },
                });
            }
            launchDuprConnect();
        },
        [dismissSpotlight, impressionUlid, prompt, launchDuprConnect, sportId],
    );

    // Platform tennis's counterpart to "Connect DUPR": open the existing
    // APTA-id dialog so the user's PTI can be resolved. Reachable only from a
    // prompt authored with the connect_apta option, so it is inert until one
    // exists.
    const handleConnectApta = React.useCallback(
        (option: HomePromptOption) => {
            dismissSpotlight();
            if (prompt) {
                recordPromptEvent({
                    eventType: "interaction",
                    prompt,
                    impressionUlid,
                    sportId,
                    metadata: {
                        action: "apta_connect",
                        answerValue: option.value,
                        answerLabel: option.label,
                    },
                });
            }
            setDialogType("APTAId");
        },
        [dismissSpotlight, impressionUlid, prompt, setDialogType, sportId],
    );

    // The "See my stats" chip on the post-assessment prompt: navigate to the
    // performance dashboard directly instead of opening the chat. The prompt is
    // gated server-side on the user actually having a VSR result to view.
    const handleSeeStats = React.useCallback(
        (option: HomePromptOption) => {
            dismissSpotlight();
            if (prompt) {
                recordPromptEvent({
                    eventType: "interaction",
                    prompt,
                    impressionUlid,
                    sportId,
                    metadata: {
                        action: "see_stats",
                        answerValue: option.value,
                        answerLabel: option.label,
                    },
                });
            }
            // /performance is the pickleball-only dashboard; the other
            // sports' stats live on their assessments list.
            navigate(statsRouteForSport(sport));
        },
        [dismissSpotlight, impressionUlid, prompt, navigate, sport, sportId],
    );

    // Close the chat overlay, logging the close so "opened then backed out" is
    // captured as a soft dismiss (analysis tells acted-vs-not via impressionUlid).
    const handleClose = React.useCallback(() => {
        if (prompt) {
            recordPromptEvent({
                eventType: "interaction",
                prompt,
                impressionUlid,
                sportId,
                metadata: { action: "overlay_close" },
            });
        }
        setOpen(false);
        setFreeTextSeed(undefined);
        // Closing a restored chat: drop the restored state and trigger a fresh
        // prompt fetch (the restore path skipped it), so the widget doesn't stay
        // frozen on the restored prompt.
        if (restored) {
            setRestored(undefined);
            setReloadNonce((n) => n + 1);
        }
    }, [impressionUlid, prompt, restored, sportId]);

    // Soft-dismissed (e.g. "Maybe later") or still loading: render nothing.
    if (dismissed || !loaded) return null;
    // Fetch resolved with no eligible prompt: hand off to the fallback.
    if (!prompt) return fallback ?? null;

    return (
        <>
            {/* First-run spotlight scrim — dims the rest of the home screen. */}
            <Fade in={spotlight} unmountOnExit>
                <Box
                    onClick={() => dismissSpotlight("scrim")}
                    sx={{
                        position: "fixed",
                        inset: 0,
                        bgcolor: "rgba(7, 12, 24, 0.66)",
                        zIndex: (t) => t.zIndex.modal - 1,
                    }}
                />
            </Fade>

            {/* Widget — lifted above the scrim while spotlit. */}
            <Box
                ref={widgetRef}
                sx={{
                    position: "relative",
                    zIndex: (t) => (spotlight ? t.zIndex.modal : "auto"),
                }}
            >
                <AskVolleyWidget
                    prompt={prompt}
                    onOpen={handleOpen}
                    onAskAnything={handleAskAnything}
                    onDismiss={handleDismiss}
                    onLaunchAssessment={handleLaunchAssessment}
                    onConnectDupr={handleConnectDupr}
                    onConnectApta={handleConnectApta}
                    onSeeStats={handleSeeStats}
                    spotlit={spotlight}
                />
                {spotlight && (
                    // Volley-green coachmark bubble that points up at the lit
                    // widget. Clicking anywhere (or "Got it") dismisses it; it
                    // never auto-settles — the user must dismiss it manually.
                    <Box
                        onClick={() => dismissSpotlight("callout")}
                        sx={{
                            position: "relative",
                            mt: 1.5,
                            mx: 1,
                            p: "12px 14px",
                            borderRadius: "12px",
                            bgcolor: "#3CE97C",
                            color: "#0B1730",
                            fontSize: 13.5,
                            fontWeight: 600,
                            lineHeight: 1.35,
                            cursor: "pointer",
                            boxShadow: "0 10px 28px rgba(0,0,0,0.4)",
                            // Diamond tail pointing up at the widget above.
                            "&::before": {
                                content: '""',
                                position: "absolute",
                                top: -7,
                                left: 26,
                                width: 14,
                                height: 14,
                                bgcolor: "#3CE97C",
                                transform: "rotate(45deg)",
                                borderRadius: "3px",
                            },
                            "@media (prefers-reduced-motion: no-preference)": {
                                animation:
                                    "askVolleySpotPopIn 320ms cubic-bezier(.2,.8,.2,1) both",
                                animationDelay: "120ms",
                                "@keyframes askVolleySpotPopIn": {
                                    from: {
                                        opacity: 0,
                                        transform:
                                            "translateY(-6px) scale(0.97)",
                                    },
                                    to: { opacity: 1, transform: "none" },
                                },
                            },
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                display: "block",
                                fontWeight: 900,
                                fontSize: 10,
                                letterSpacing: "0.06em",
                                textTransform: "uppercase",
                                opacity: 0.7,
                                mb: "3px",
                            }}
                        >
                            Start here
                        </Box>
                        Get the most out of your trainer — tap for content
                        picked just for you.
                        <Box
                            component="button"
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                dismissSpotlight("got_it");
                            }}
                            sx={{
                                display: "block",
                                mt: "10px",
                                p: 0,
                                border: 0,
                                background: "none",
                                color: "rgba(11,23,48,0.55)",
                                fontFamily: "inherit",
                                fontWeight: 900,
                                fontSize: 11,
                                letterSpacing: "0.05em",
                                textTransform: "uppercase",
                                cursor: "pointer",
                            }}
                        >
                            Got it
                        </Box>
                    </Box>
                )}
            </Box>

            {open && (
                <AskVolleyOverlay
                    prompt={prompt}
                    impressionUlid={impressionUlid}
                    sportName={sport}
                    sportId={sportId}
                    initialOptionValue={initialOption}
                    freeTextSeed={freeTextSeed}
                    restored={restored}
                    onClose={handleClose}
                    onLaunchAssessment={handleLaunchAssessment}
                    onConnectDupr={handleConnectDupr}
                    onConnectApta={handleConnectApta}
                    onSeeStats={handleSeeStats}
                />
            )}

            {/* Assessment start dialog (owned here so it survives the overlay
                closing or the widget launching it without opening the chat). */}
            {assessmentDialog}

            {/* DUPR connect dialog (owned here for the same reason — the profile
                prompt's "Connect DUPR" chip opens it without entering chat). */}
            {duprDialog}
        </>
    );
}
